import { float, max, min, mx_noise_float, saturation, smoothstep, vec3 } from 'three/tsl';
import { Vector3 } from 'three/webgpu';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import type { CloudDef } from '../../engine/atmosphere/CloudLayer';
import type { GlowDef, LightRig } from '../../engine/lighting/LightRig';
import { hashSeed, mulberry32 } from '../../engine/math/rng';
import { BiomeField, type BiomeFieldDef, calibrateBiomes } from '../../engine/terrain/BiomeField';
import { biomeWeight, mixStyles } from '../../engine/terrain/mixStyles';
import { scaleProfile, type TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { bakeKey, type TerrainStyle, type WaterStyle } from '../../engine/terrain/TerrainStyle';
import { TerrainHeights } from '../../engine/terrain/terrainHeight';
import { G0, type SunDef } from '../../engine/universe/HollowDef';
import type { GrassStyle, SpeciesStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import { CAVE_HINTS } from '../hollows/caves';
import { EMBERDEEP } from '../hollows/emberdeep';
import { GLOWMIRE } from '../hollows/glowmire';
import { VERDANT } from '../hollows/verdant';
import type { CaveDef, CaveEndDef } from '../scenes/CaveWorld';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { BIOMES, type Biome, PLANET_TYPES, SKIES, type Sky, SPECIES } from './catalog';
import { type Placement, placeUniverse } from './placement';
import {
  hexToLinear,
  SMALL_WORLD,
  type SpeciesId,
  type UniverseSpec,
  type WhereId,
  type WorldSpec,
  walkFromStart,
} from './spec';

type N = any;
type RGB = [number, number, number];

/** A custom universe, ready to load. */
export interface BuiltUniverse {
  /** The worlds, in the spec's order. */
  hollows: HollowSceneDef[];
  /** With two worlds or more: the caves between them (its hollows start with the start world). */
  cave: CaveDef | null;
  /** Where the worlds sit and the caves open (spec order). */
  placement: Placement;
  /** Each world's terrain recipe and biome field (null for one biome), for the editor's map. */
  terrains: TerrainProfile[];
  fields: (BiomeField | null)[];
  /** What the editor should mention (a cave that had to be longer than asked for). */
  notes: string[];
}

/** Every world a few thousand km from the origin, as the built-in ones (f32 slips show at once). */
const DISTANCE = 9e6;
/** Sun radius as a share of the world's. */
const SUN_SIZE = 0.015;
/** Clouds as Verdant's, as shares of its radius (smaller worlds), or growing slowly past it. */
const CLOUDS = { altitude: 2100, scale: 2600, drift: 0.004, radius: 12_000 };
/** How each "where" leans a biome's score: toward high ground (+height) or the poles (+polar). */
const LEAN: Record<WhereId, { height: number; polar: number }> = {
  anywhere: { height: 0, polar: 0 },
  low: { height: -1.6, polar: 0 },
  high: { height: 1.6, polar: 0 },
  poles: { height: 0, polar: 1.6 },
  equator: { height: 0, polar: -1.6 },
};

/** Build the universe a spec describes. Deterministic: the same spec gives the same worlds and caves. */
export function buildUniverse(u: UniverseSpec): BuiltUniverse {
  const terrains = u.worlds.map(terrainOf);
  const fields = u.worlds.map((w, i) => biomeFieldOf(w, terrains[i]));
  const heights = u.worlds.map((w, i) => new TerrainHeights(terrains[i], w.seed, w.radius));
  const rnd = mulberry32(hashSeed(u.seed, 1));
  const d0 = new Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).normalize();
  const walk = walkFromStart(u);
  const placement = placeUniverse(
    u.worlds.map((w, i) => ({
      radius: w.radius,
      seed: w.seed,
      terrain: terrains[i],
      dry: (dir: Vector3) => dryAt(w, terrains[i], fields[i], dir, heights[i]),
    })),
    walk.order.slice(1).map((to) => ({ from: walk.parent[to], to, gap: u.caves[walk.cave[to]].length })),
    d0.multiplyScalar(DISTANCE),
    u.seed,
    u.start,
  );
  const hollows = u.worlds.map((w, i) => {
    const at =
      i === u.start
        ? placement.spawn.dir
        : placement.links.find((l) => l.from === i || l.to === i)?.mouths[0];
    const dry = dryAt(w, terrains[i], fields[i], at ?? placement.spawn.dir, heights[i]);
    return hollowOf(w, i, u.start, terrains[i], fields[i], placement, dry);
  });
  const notes = placement.links
    .filter((l) => l.grown)
    .map(
      (l) =>
        `The cave from ${u.worlds[l.from].name} to ${u.worlds[l.to].name} had to be ${Math.round(l.gap)} m long for ${u.worlds[l.to].name} to fit.`,
    );
  const hints = [`${u.name}: worlds of your own making.`];
  if (hollows.length === 1) {
    hollows[0].hints = hints;
    return { hollows, cave: null, placement, terrains, fields, notes };
  }
  // CaveWorld starts in its first hollow: the start world, then the others as they're reached.
  const index = new Map(walk.order.map((w, k) => [w, k]));
  const end = (wi: number, dir: Vector3, height: number): CaveEndDef => {
    const w = u.worlds[wi];
    const b = BIOMES[w.biomes[dominantBiome(fields[wi], dir, height)].id];
    return { height, leak: SKIES[w.sky].leak, glow: b.mouth.glow, rocks: b.mouth.rocks };
  };
  const cave: CaveDef = {
    id: `custom-${u.seed}`,
    name: u.name,
    hollows: walk.order.map((i) => hollows[i]),
    links: placement.links.map((l) => ({
      from: index.get(l.from)!,
      to: index.get(l.to)!,
      axis: l.axis,
      tunnel: l.tunnel,
      spiralGap: 0,
      phaseB: l.phaseB,
      gap: l.gap,
      a: end(l.from, l.mouths[0], l.heights[0]),
      b: end(l.to, l.mouths[1], l.heights[1]),
    })),
    hints: [...hints, ...CAVE_HINTS],
  };
  return { hollows, cave, placement, terrains, fields, notes };
}

/** Size relative to the planet type's own, and how heights follow it: in step when smaller, gentler when bigger. */
function sizeOf(w: WorldSpec): { size: number; grow: number } {
  const size = w.radius / PLANET_TYPES[w.type].radius;
  return { size, grow: size <= 1 ? size : Math.sqrt(size) };
}

/** The planet type's terrain recipe at this world's size, with the spec's changes. */
export function terrainOf(w: WorldSpec): TerrainProfile {
  const base = PLANET_TYPES[w.type].terrain;
  const { size, grow } = sizeOf(w);
  const p = scaleProfile(base, size * w.landSize, grow * w.relief);
  const atLeast = (l: TerrainProfile['continents'], m: number) => ({
    ...l,
    wavelength: Math.max(l.wavelength, m),
  });
  return {
    ...p,
    continents: atLeast(p.continents, 120),
    mountainMask: atLeast(p.mountainMask, 120),
    ridges: atLeast(p.ridges, 50),
    landBias: base.landBias - 0.35 * w.water,
    ridgeHeight: p.ridgeHeight * w.mountains,
    ridgeDamping: w.erosion,
    hillHeight: p.hillHeight * w.hills,
    detailHeight: base.detailHeight * w.rough,
    snowLine: w.snow ? w.snowLine : 1e9,
    // (Palettes divide heights by this: never by 0.)
    heightScale: Math.max(p.heightScale ?? 1, 0.02),
  };
}

/**
 * How the world's ground is shared out between its biomes (null for one biome): regions about
 * a world's radius across, leaning as each biome's "where" asks, with offsets found so each
 * biome gets about its share of the ground.
 */
export function biomeFieldOf(w: WorldSpec, terrain: TerrainProfile): BiomeField | null {
  if (w.biomes.length < 2) return null;
  const axis = new Vector3(...SKIES[w.sky].veil.spinAxis).normalize();
  const def: BiomeFieldDef = {
    wavelength: 0.7 * w.radius * w.biomeSize,
    // (24 drew borders a hundredth of a region wide: hard edges, like paint on a map.)
    sharpness: 14 / w.biomeBlend,
    biomes: w.biomes.map((b) => ({ offset: 0, ...LEAN[b.where] })),
    axis: [axis.x, axis.y, axis.z],
    heightRef: Math.max(5, 0.35 * (terrain.continentHeight * (1 + terrain.landBias) + terrain.ridgeHeight)),
    radius: w.radius,
    seed: w.seed,
  };
  const heights = new TerrainHeights(terrain, w.seed, w.radius);
  const samples: { dir: Vector3; h: number }[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < 900; i++) {
    const y = 1 - (2 * (i + 0.5)) / 900;
    const r = Math.sqrt(1 - y * y);
    const dir = new Vector3(r * Math.cos(i * golden), y, r * Math.sin(i * golden));
    samples.push({ dir, h: heights.sample(dir, w.radius / 40).height });
  }
  const offsets = calibrateBiomes(
    new BiomeField(def),
    samples,
    w.biomes.map((b) => b.share),
  );
  def.biomes.forEach((b, i) => {
    b.offset = offsets[i];
  });
  return new BiomeField(def);
}

/** The biome with the most weight at `dir` over ground `h` metres high. */
function dominantBiome(field: BiomeField | null, dir: Vector3, h: number): number {
  if (!field) return 0;
  const w = field.weights(dir, h);
  return w.indexOf(Math.max(...w));
}

/** How high above the water a mouth or the start must be at `dir`: its biome's margin, at the world's scale. */
function dryAt(
  w: WorldSpec,
  t: TerrainProfile,
  field: BiomeField | null,
  dir: Vector3,
  heights: TerrainHeights,
): number {
  const h = field ? heights.sample(dir, w.radius / 200).height : 0;
  const b = BIOMES[w.biomes[dominantBiome(field, dir, h)].id];
  const margin = Math.max(b.dry, w.sea === 'lava' ? 8 : 0);
  return Math.max(1, margin * (t.heightScale ?? 1));
}

function hollowOf(
  w: WorldSpec,
  i: number,
  start: number,
  terrain: TerrainProfile,
  field: BiomeField | null,
  placed: Placement,
  dry: number,
): HollowSceneDef {
  const sky = SKIES[w.sky];
  const R = w.radius;
  const { grow } = sizeOf(w);
  const slots = w.biomes;
  const biomes = slots.map((s) => BIOMES[s.id]);
  const multi = field !== null;
  const sun: SunDef | null = w.sun
    ? { radius: SUN_SIZE * R * w.sunSize, color: hexToLinear(w.sunColor), intensity: w.sunStrength }
    : null;
  const glow: GlowDef | undefined =
    w.glow > 0
      ? { color: hexToLinear(w.glowColor), day: w.glow, night: w.glowNight, pulse: sky.glow?.pulse }
      : undefined;
  const air = w.haze > 0 && sky.air ? scaleAir(sky.air, R / sky.airRadius, w.haze) : null;
  const clouds: CloudDef | null =
    air && w.clouds > 0
      ? {
          altitude: cloudScale(R, 1, 0.15) * CLOUDS.altitude * w.cloudHeight,
          scale: cloudScale(R, 1, 0.7) * CLOUDS.scale,
          coverage: w.clouds,
          drift: CLOUDS.drift,
        }
      : null;
  const shares = slots.map((s) => s.share);
  const shareSum = shares.reduce((a, b) => a + b, 0) || 1;
  const tint = hexToLinear(w.groundTint);
  const albedo = [0, 1, 2].map(
    (k) =>
      (slots.reduce((a, _, j) => a + biomes[j].albedo[k] * shares[j], 0) / shareSum) *
      tint[k] *
      w.groundBrightness,
  ) as RGB;

  // Per light rig: each biome's ground (forests sized to the world), and all of it mixed and graded.
  const forestScale = grow * w.forestSize;
  const own = perRig((rig) => biomes.map((b) => forestWrap(b.style(rig), forestScale, w.forests)));
  const style = perRig((rig) => grade(mixStyles(own(rig), waterOf(w, own(rig)[0], rig)), w));
  const leaves = hexToLinear(w.foliageTint);
  const species = (id: SpeciesId, rig: LightRig): SpeciesStyle => {
    const s = SPECIES[id].make(rig);
    return {
      ...s,
      crown: [mul(s.crown[0], leaves), mul(s.crown[1], leaves)],
      scale: (s.scale ?? 1) * w.treeSize,
    };
  };
  // Each biome's trees, a layer of their own: its forests, where the biome is.
  const treesOf =
    (k: number) =>
    (rig: LightRig): TreeStyle => {
      const b = biomes[k];
      const base = b.trees?.(rig) ?? { aShare: [0.5, 0.5] as [number, number], loneTrees: 0 };
      const grows = b.trees !== null && w.trees > 0;
      const mask = own(rig)[k].forestMask;
      return {
        a: species(slots[k].treeA, rig),
        b: species(slots[k].treeB, rig),
        aShare: base.aShare,
        // (Never quite 0: the renderer's thresholds would meet.)
        loneTrees: Math.max(base.loneTrees * w.trees, 1e-4),
        minHeight: grows ? base.minHeight : 1e9,
        mask: w.trees === 1 ? mask : (ctx) => min(mask(ctx).mul(w.trees), 1),
        where: treesWhere(multi, k, biomes[k].snowLine),
      };
    };
  const rocksOf = biomes.find((b) => b.rocks)?.rocks;

  // Where a world on its own starts, and (in a cave world) near its first mouth.
  const first = placed.links.find((l) => l.from === i || l.to === i);
  const spawnDir = i === start || !first ? placed.spawn.dir : first.mouths[first.from === i ? 0 : 1];
  const c = placed.centers[i];
  return {
    id: `custom-${i}`,
    name: w.name,
    center: [c.x, c.y, c.z],
    radius: R,
    seed: w.seed,
    gravity: w.gravity > 0 ? w.gravity * G0 : undefined,
    sun,
    terrain,
    air,
    clouds,
    veil: {
      ...sky.veil,
      litFraction: w.daylight,
      dayLength: w.dayLength,
      duskColor: hexToLinear(w.duskColor),
    },
    glow,
    worldAlbedo: albedo,
    style,
    biomes: field?.def,
    grass: grassOf(w, biomes),
    trees: treesOf(0),
    treesBiome: multi ? 0 : undefined,
    biomeTrees: multi ? biomes.slice(1).map((_, k) => ({ biome: k + 1, trees: treesOf(k + 1) })) : undefined,
    rocks: w.rocks ? (rocksOf ?? undefined) : undefined,
    birds: w.birds ? birdsFor(R) : undefined,
    motes: w.motes === 'none' ? undefined : MOTES[w.motes],
    spawn: {
      dir: [spawnDir.x, spawnDir.y, spawnDir.z],
      minHeight: dry,
      maxHeight: Math.max(2 * placed.spawn.height, dry + 30),
    },
    ambience: sky.ambience,
    lantern: w.lantern,
    startHour: w.startHour,
    exposureKey: exposureKey(w, sky, albedo) * w.exposure,
    atlasScale: R < SMALL_WORLD ? 0.5 : undefined,
    // Fade palette noise a pixel can't show. The biome palettes' fine patches (dry grass,
    // which on snowy biomes is snow) speckled the far side of even small worlds.
    filterPalette: true,
    waterSkyTint: sky.waterSkyTint,
  };
}

/** What drifts in the air near you: the built-in worlds' fireflies, spores and embers. */
const MOTES = { fireflies: VERDANT.motes!, spores: GLOWMIRE.motes!, embers: EMBERDEEP.motes! };

/**
 * The world's grass: one biome's, or every biome's blended blade by blade (colours, height,
 * and how thickly it grows: sparse biomes grow it in tufts).
 */
function grassOf(w: WorldSpec, biomes: Biome[]): HollowSceneDef['grass'] {
  const t = hexToLinear(w.grassTint);
  return (rig) => {
    const gs = biomes.map((b) => b.grass(rig));
    const minHeight = Math.min(...gs.map((g) => g.minHeight ?? 3.5));
    const base: GrassStyle = {
      ...gs[0],
      lush: mul(gs[0].lush, t),
      meadow: mul(gs[0].meadow, t),
      tip: mul(gs[0].tip, t),
      height: Math.max(w.grass, 0.01),
      minHeight: w.grass > 0 && biomes.some((b) => b.grassAmount > 0) ? minHeight : 1e9,
    };
    const sparse = biomes.some((b) => b.grassAmount < 1);
    // Biomes whose palettes lay snow on the ground (their own snow line, or snowy dry patches):
    // grass doesn't grow through it (blades stood all over the snow).
    const snowy = biomes.some((b) => b.snowPatches !== undefined || b.snowLine !== undefined);
    if (biomes.length === 1 && !sparse && !snowy)
      return { ...base, height: (gs[0].height ?? 1) * Math.max(w.grass, 0.01) };
    const glows = gs.map((g) => g.glow);
    const tipGlows = gs.map((g) => g.tipGlow);
    return {
      ...base,
      local: ({ p, h, biome }) => {
        const tufts = sparse ? mx_noise_float(p.mul(0.9)).mul(0.5).add(0.5) : float(1);
        // The palette's 95 m and 11 m noises (biomePalette's fineT and patchT): they bend its snow
        // line and lay its dry patches.
        const fine = snowy ? mx_noise_float(p.mul(1 / 95)) : null;
        const patch = biomes.some((b) => b.snowPatches !== undefined) ? mx_noise_float(p.mul(1 / 11)) : null;
        let lush: N = vec3(0);
        let meadow: N = vec3(0);
        let tip: N = vec3(0);
        let growth: N = float(0);
        let height: N = float(0);
        gs.forEach((g, k) => {
          const wk: N = biome ? biomeWeight(biome, k) : float(k === 0 ? 1 : 0);
          const amount = biomes[k].grassAmount;
          const minH = g.minHeight ?? 3.5;
          let here: N = (
            amount >= 1 ? float(1) : smoothstep(1 - amount - 0.08, 1 - amount + 0.08, tufts)
          ).mul(smoothstep(minH, minH * 1.7, h));
          const line = biomes[k].snowLine;
          if (line !== undefined && fine)
            here = here.mul(float(1).sub(smoothstep(line - 110, line - 40, h.add(fine.mul(60)))));
          const patches = biomes[k].snowPatches;
          if (patches !== undefined && fine && patch)
            here = here.mul(
              float(1).sub(
                smoothstep(0.35 - 0.3 * patches, 0.75 - 0.3 * patches, patch.mul(0.6).add(fine.mul(0.4))),
              ),
            );
          lush = lush.add(vec3(...mul(g.lush, t)).mul(wk));
          meadow = meadow.add(vec3(...mul(g.meadow, t)).mul(wk));
          tip = tip.add(vec3(...mul(g.tip, t)).mul(wk));
          growth = growth.add(here.mul(wk));
          height = height.add(wk.mul(g.height ?? 1));
        });
        return { lush, meadow, tip, grow: growth, height };
      },
      glow: glows.some(Boolean) ? (biome?: N) => weighted(glows, biome) : undefined,
      tipGlow: tipGlows.some(Boolean) ? (biome?: N) => weighted(tipGlows, biome) : undefined,
    };
  };
}

/** Glow closures of each biome's grass, weighted by the biome weights (the first alone without them). */
function weighted(fs: (((biome?: N) => N) | undefined)[], biome?: N): N {
  let out: N = vec3(0);
  fs.forEach((f, k) => {
    if (!f) return;
    out = out.add(biome ? f().mul(biomeWeight(biome, k)) : k === 0 ? f() : vec3(0));
  });
  return out;
}

/** The world's seas: water (the first biome's, recoloured) or lava (Emberdeep's). */
function waterOf(w: WorldSpec, first: TerrainStyle, rig: LightRig): WaterStyle {
  const base = w.sea === 'lava' ? EMBERDEEP.style(rig).water : first.water;
  return { ...base, shallow: hexToLinear(w.waterShallow), deep: hexToLinear(w.waterDeep) };
}

/** One value per light rig (a world's builders are called with its rig; share what they make). */
function perRig<T>(make: (rig: LightRig) => T): (rig: LightRig) => T {
  const made = new WeakMap<LightRig, T>();
  return (rig) => {
    let v = made.get(rig);
    if (v === undefined) {
      v = make(rig);
      made.set(rig, v);
    }
    return v;
  };
}

/**
 * Where a biome's trees may grow: its share of the ground (several biomes), and below its tree
 * line (from 55% of its snow line, gone by 80%), lone trees too: they stood on the snowfields.
 */
function treesWhere(multi: boolean, k: number, snowLine: number | undefined): TreeStyle['where'] {
  if (!multi && snowLine === undefined) return undefined;
  return (ctx) => {
    let w: N = multi ? (ctx.biome ? biomeWeight(ctx.biome, k) : float(k === 0 ? 1 : 0)) : float(1);
    if (snowLine !== undefined) w = w.mul(float(1).sub(smoothstep(snowLine * 0.55, snowLine * 0.8, ctx.h)));
    return w;
  };
}

/**
 * A biome's ground with its forests resized (`scale` times its own patches) and thinned or
 * thickened; its palette sees these forests (it runs with the wrapper as `this`).
 */
function forestWrap(base: TerrainStyle, scale: number, amount: number): TerrainStyle {
  if (scale === 1 && amount === 1) return base;
  // Its forests' noise at p / scale is the same noise at p with wavelengths scale times longer:
  // those are the terms the tiles carry (TerrainStyle.bakedNoise), looked up under the names the
  // palette asks for.
  const forestTerms = (base.bakedNoise ?? []).filter((t) => t.forest);
  const style: TerrainStyle = {
    bakedNoise: base.bakedNoise?.map((t) => (t.forest ? { ...t, wavelength: t.wavelength * scale } : t)),
    forestMask(ctx) {
      let baked: Map<string, N> | undefined;
      if (scale !== 1 && ctx.baked) {
        baked = new Map();
        for (const t of forestTerms) {
          const v = ctx.baked.get(bakeKey(t.wavelength * scale, t.offset));
          if (v) baked.set(bakeKey(t.wavelength, t.offset), v);
        }
      }
      const c =
        scale === 1
          ? ctx
          : { ...ctx, p: ctx.p.mul(1 / scale), footprint: ctx.footprint?.mul(1 / scale), baked };
      const m = base.forestMask(c);
      return amount === 1 ? m : min(m.mul(amount), 1);
    },
    land: (ctx) => base.land.call(style, ctx),
    water: base.water,
  };
  return style;
}

/** The world's ground graded as a whole: tint, brightness, saturation, and its glow. */
function grade(base: TerrainStyle, w: WorldSpec): TerrainStyle {
  const tint = hexToLinear(w.groundTint).map((c) => c * w.groundBrightness) as RGB;
  const graded = tint.some((c) => c !== 1);
  const glow = w.groundGlow;
  const water = base.water;
  return {
    bakedNoise: base.bakedNoise,
    forestMask: base.forestMask,
    land(ctx) {
      const s = base.land(ctx);
      let albedo: N = s.albedo;
      if (w.groundSaturation !== 1) albedo = max(saturation(albedo, w.groundSaturation), 0);
      if (graded) albedo = min(albedo.mul(vec3(...tint)), 0.95);
      return { ...s, albedo, emissive: s.emissive && glow !== 1 ? s.emissive.mul(glow) : s.emissive };
    },
    water: {
      ...water,
      emissive: water.emissive && glow !== 1 ? (ctx) => water.emissive!(ctx).mul(glow) : water.emissive,
    },
  };
}

/**
 * The sky's air at this size (`size`: the world's radius over the sky's own): a smaller world
 * gets denser air over a thinner layer, so its sky across keeps the colour; a bigger one keeps
 * the density.
 */
function scaleAir(a: AtmosphereDef, size: number, haze: number): AtmosphereDef {
  const dense = (size <= 1 ? 1 / size : 1) * haze;
  const deep = size <= 1 ? size : size ** 0.3;
  return {
    ...a,
    rayleigh: a.rayleigh.map((x) => x * dense) as RGB,
    rayleighHeight: Math.max(300, a.rayleighHeight * deep),
    mie: a.mie * dense,
    mieHeight: Math.max(100, a.mieHeight * deep),
    airGlow: a.airGlow?.map((x) => x * dense) as RGB | undefined,
  };
}

/** Clouds scale with the world up to Verdant's size, then by `bigPower`. */
function cloudScale(R: number, smallPower: number, bigPower: number): number {
  const k = R / CLOUDS.radius;
  return k <= 1 ? k ** smallPower : k ** bigPower;
}

/**
 * Exposure at full daylight: the sky's, by how much brighter or dimmer the light is. A sun
 * dominates any glow. Pale ground (sand, snow) takes less opening up than dark, much as a
 * camera meters a scene: the palettes are near real albedos (snow some five times brighter
 * than grass), so a snowfield or a desert would otherwise wash out. With neither sun nor glow,
 * the lantern is the light.
 */
function exposureKey(w: WorldSpec, sky: Sky, albedo: RGB): number {
  const mean = (a: RGB) => (a[0] + a[1] + a[2]) / 3;
  if (w.sun) {
    const key = sky.sun
      ? sky.exposureKey * (sky.sun.intensity / w.sunStrength)
      : VERDANT.exposureKey * (VERDANT.sun!.intensity / w.sunStrength);
    const dark = mean(VERDANT.worldAlbedo) / Math.max(mean(albedo), 0.01);
    return key * Math.min(Math.max(dark, 0.3), 3) ** 0.75;
  }
  if (w.glow > 0) {
    return sky.glow ? sky.exposureKey * (sky.glow.day / w.glow) ** 0.7 : 7 * (0.7 / w.glow) ** 0.7;
  }
  return sky.sun ? 3 : sky.exposureKey;
}

/** Birds sized to the sky: Verdant's flocks, or a few low ones in a small world (as the Geodes'). */
function birdsFor(R: number): HollowSceneDef['birds'] {
  return (rig) => {
    const b = VERDANT.birds!(rig);
    if (R >= SMALL_WORLD) return b;
    return { ...b, flocks: 4, perFlock: 5, altitude: [8, 24], radius: [10, 22], speed: 7 };
  };
}

function mul(a: RGB, b: RGB): RGB {
  return [a[0] * b[0], a[1] * b[1], a[2] * b[2]];
}
